"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SiemensSolutionsGrid() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const rooms = [{
    numeral: "I",
    name: "Sinumerik One",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/siemens_sinumerik_one.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/siemens_sinumerik_one.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/siemens_sinumerik_one.webp-wvc-sizes",
    alt: "Siemens Sinumerik One CNC control system",
    features: ["HIGH PERFORMANCE", "DIGITAL TWIN", "ADVANCED CNC"]
  }, {
    numeral: "II",
    name: "Manage MyMachine",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/siemens_manage_mymachine.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/siemens_manage_mymachine.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/siemens_manage_mymachine.webp-wvc-sizes",
    alt: "Siemens Manage MyMachine software interface",
    features: ["CLOUD CONNECTIVITY", "REAL-TIME DATA", "OPTIMIZATION"]
  }, {
    numeral: "III",
    name: "Powerline Modules",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/siemens_powerline_modules.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/siemens_powerline_modules.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/siemens_powerline_modules.png-wvc-sizes",
    alt: "Siemens Powerline industrial modules",
    features: ["ROBUST DESIGN", "HIGH EFFICIENCY", "INDUSTRIAL GRADE"]
  }];
  return <section data-section-id="192" id="siemenssolutionsgrid" className="bg-background text-foreground py-20 md:py-32 lg:py-[8rem] overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`mb-16 md:mb-24 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex flex-col">
            <span className="font-default text-xs tracking-[0.3em] uppercase mb-12 md:mb-16 text-primary">
              03 / SOLUTIONS
            </span>

            <h2 className="font-default text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-light leading-[1.05] tracking-tight text-center mx-auto text-foreground">
              Precision engineering
              <br />
              for modern industry.
            </h2>

            <p className="mt-10 md:mt-12 mx-auto text-center text-base md:text-lg leading-relaxed font-default max-w-[480px] text-muted-foreground">
              IMCNC Designs provides expert integration and support for Siemens industrial systems. From advanced CNC controls to real-time machine monitoring, we ensure your production remains at peak performance.
            </p>
          </div>
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 lg:gap-12">
          {rooms.map((room, i) => <div data-index={i} key={room.numeral} className={`group flex flex-col transition-all duration-1000 ease-out hover:-translate-y-1 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
          transitionDelay: gridVisible ? `${i * 150}ms` : "0ms"
        }}>
              {}
              <div className="relative overflow-hidden mb-8 aspect-[4/5] rounded-lg shadow-md">
                <img src={room.image} data-wvc-srcset={room.srcset} data-wvc-sizes={room.sizes} alt={room.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]" />
              </div>

              {}
              <div className="flex flex-col">
                {}
                <span className="font-default text-base mb-4 tracking-wider text-primary">
                  {room.numeral}
                </span>

                {}
                <h3 className="font-default text-2xl md:text-3xl font-light leading-tight mb-5 relative inline-block w-fit text-foreground">
                  {room.name}
                  <span className="absolute -bottom-1 left-0 h-px w-0 group-hover:w-full transition-all duration-500 ease-out bg-primary" />
                </h3>

                {}
                <div className="h-px w-12 mb-6 bg-primary/50" />

                {}
                <div className="flex flex-wrap gap-2 mb-8">
                  {room.features.map((feature, j) => <span data-index={j} key={feature} className="inline-flex items-center px-3 py-1.5 rounded-md border text-[10px] font-default tracking-[0.15em] uppercase border-primary/40 text-muted-foreground">
                      {feature}
                    </span>)}
                </div>

                {}
                <div className="flex items-end justify-between mt-auto pt-2">
                  <Link to={i === 0 ? "/SiemensSinumerikOnePage" : i === 1 ? "/SiemensManageMyMachinePage" : "/SiemensPowerlinePage"} className="group/link inline-flex items-center gap-2 font-default text-xs tracking-[0.2em] uppercase transition-colors duration-300 text-foreground/75">
                    <span>View details</span>
                    <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover/link:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>)}
        </div>

        {}
        
      </div>
    </section>;
}